import update from 'immutability-helper';
import _, { round } from 'lodash';
import React, { FC, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { fetchQuery, graphql, useLazyLoadQuery, useMutation, useRelayEnvironment } from 'react-relay/hooks';
import { ActionMeta, CSSObjectWithLabel, OptionProps, SingleValue } from 'react-select';
import {
    DeletedAreaIdentifier,
    ModelArea,
    ModelAreaLayer,
    createAreaFromBoundsPX,
    createAreaFromModelAreaLayer,
    createAreaFromOutlinesResult,
    createAreaFromPaperItem,
    createAreaFromSizeInCM,
    createAreaLayerFromTemplateLayer,
    createDefaultCenteredArea,
    getLayerColor,
    resizeAreaInCM
} from 'src/components/pages/productConfiguration/state/model/modelArea';
import { ModelSide } from 'src/components/pages/productConfiguration/state/model/modelSide';
import { AIGeneratingIndicator, AIGradientBanner } from 'src/components/widgets/ai';
import { AreaDesignerViewer } from 'src/components/widgets/areaDesigner/areaDesignerViewer';
import { Input } from 'src/components/widgets/input';
import Sparkles from 'src/components/widgets/svg/sparkles';
import { T, openFileDialog, stringFormat, uuid, validateInputSecurity } from 'src/shared/helpers';
import MdTimeline from 'src/shared/material_icons/action/join_inner.svg';
import MdPanTool from 'src/shared/material_icons/action/pan_tool.svg';
import MdLink from 'src/shared/material_icons/content/link.svg';
import DownArrowIcon from 'src/shared/material_icons/hardware/keyboard_arrow_down.svg';
import UpArrowIcon from 'src/shared/material_icons/hardware/keyboard_arrow_up.svg';
import MdCropPortrait from 'src/shared/material_icons/image/crop_square.svg';
import PDFIcon from 'src/shared/material_icons/image/picture_as_pdf.svg';
import MdStraighten from 'src/shared/material_icons/image/straighten.svg';
import MdRadioButtonUnchecked from 'src/shared/material_icons/toggle/radio_button_unchecked.svg';
import MdStar from 'src/shared/material_icons/toggle/star.svg';
import { findOutlines } from 'src/shared/utilities/outline/outliner';
import { Outline } from 'src/shared/utilities/outline/outliner_worker';
import styled from 'styled-components';
import { ValidationStateProvider, validationContext } from '../../../shared/validation';
import { DisabledModelSide } from '../../pages/productConfiguration/state';
import { ModelColor } from '../../pages/productConfiguration/state/model/modelColor';
import { ModelOptions } from '../../pages/productConfiguration/state/model/modelOptions';
import { AiIconButton } from '../ai';
import { Button, Link, PrimaryButton } from '../buttons';
import { TextButton } from '../buttons/textButton';
import { Checkbox } from '../checkbox';
import { Dialog, MessageDialog, QuestionDialog, useDialogManager } from '../dialog';
import { FormControl } from '../formControl';
import { Icon, IconProps } from '../icon';
import { showErrorNotification, showNotification, showWarningNotification } from '../notificationToaster';
import { AdvancedSelect } from '../select';
import { TextArea } from '../textArea';
import UploadProgressRow from '../uploadProgressRow';
import { ZTooltip } from '../ztooltip';
import { areaDesignerQuery } from './__generated__/areaDesignerQuery.graphql';
import { Area, AreaLayer } from './areaDesigner.model';
import { Tool, Unit, getStartingUnit, units } from './helpers';
import LayersUploadDialog from './layersUploadDialog';
import { EditableModelOptions, createDefaultEditableModelOptions } from './models';
import { editableModelOptionsToModelOptions, modelOptionsToEditableModelOptions } from './modelsAdapters';

import MaterialDeleteSvg from 'src/shared/material_icons/action/delete.svg';
import Stack from '../stack/stack';
import RulerInput from './rulerInput';
const AreaDesignerLayout = styled.div`
    display: grid;
    grid-template-columns: 1fr 280px;
    grid-gap: 32px;
    user-select: none;
    overflow: hidden;
    min-height: 0;
    flex: 1 1 auto;
    z-index: 2002;
`;

const AreaDesignerViewerContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

// Match the banner width to the viewer (canvas) column only:
// full width minus the 280px side panel and its 32px gap.
const AutoGenerateBannerRow = styled.div`
    width: calc(100% - 312px);
    flex-shrink: 0;
`;

const TemplateOptionActionIcon = styled(Icon)``;

const AreaDesignerRightPanel = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: auto;
    font-size: 13px;
    color: #263238;
`;

const AreaDesignerRightPanelFooter = styled.div`
    margin-top: auto;

    ${Link} {
        display: block;
        text-align: center;
        margin-bottom: 10px;
    }

    ${Button} {
        margin-bottom: 10px;
    }
`;

const SizeColumns = styled.div`
    display: grid;
    grid-gap: 8px;
    grid-template-columns: 1fr 16px 1fr auto;
    margin-bottom: 18px;
    align-items: end;

    > .apply-btn {
        align-self: end;
        height: 28px;
        display: flex;
        align-items: center;
    }
`;

const LayersList = styled.ul`
    margin: 0;
    margin-bottom: 20px;
    padding: 0;
    list-style: none;
    overflow: auto;
    min-height: 0;
`;

const LayerHeader = styled.div`
    display: flex;
    padding: 12px 0;
    border-bottom: 1px #e5e7eb solid;
    cursor: pointer;

    &:hover {
        background-color: #f9fafb;
    }

    ${Icon} {
        width: 18px;
        height: 18px;
        margin-left: auto;
        margin-right: 10px;
    }
`;

const LayerContent = styled.div`
    overflow: hidden;
`;

const LayerColorBox = styled.div<{ color: string }>`
    width: 16px;
    height: 16px;
    background-color: ${(props) => props.color};
    display: inline-block;
    border: 1px #e5e7eb solid;
    border-radius: 3px;
    margin-right: 12px;
`;

const Layer = styled.li<{ opened: boolean }>`
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    font-size: 12px;

    ${Input}, ${TextArea} {
        font-size: 12px;
        padding: 5px;
        margin-bottom: 5px;
        height: auto;
    }

    ${LayerContent} {
        height: ${(props) => (props.opened ? 'auto' : '0px')};
        padding: ${(props) => (props.opened ? '15px 0 15px 30px' : '0')};
    }
`;

const LayerSubOption = styled.div`
    padding-left: 25px;
`;

type LinkIconProps = { active: boolean } & IconProps;

const LinkIcon = styled<FC<LinkIconProps>>(Icon)`
    width: 16px;
    height: 16px;
    min-width: 16px;
    cursor: pointer;
    color: #9ca3af;
    align-self: end;
    justify-self: center;
    margin-bottom: 6px;
    ${(props) => props.active && `color: ${props.theme.app.secondary};`}
`;

const TemplateOptionDeleteIcon = styled.button`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 4px;
    background: none;
    color: #263238;
    cursor: pointer;
    padding: 0;
    transition: all 0.15s;

    &:hover {
        background: #f3f4f6;
    }
`;

const TemplateOption: FC<OptionProps<{ value: number; isDefault: boolean; label: string }, false>> = (props) => {
    return (
        <div
            style={{
                ...(props.getStyles('option', props) as Record<string, string>),
                position: 'relative',
                paddingRight: 30
            }}
            {...props.innerProps}
        >
            {props.data.label}

            {!props.data.isDefault && (
                <TemplateOptionDeleteIcon
                    title={T._('Delete template', 'Admin')}
                    onClick={(e) => {
                        e.stopPropagation();
                        props.selectProps.onChange?.(props.data, { action: 'remove-value', removedValue: props.data });
                    }}
                >
                    <TemplateOptionActionIcon>
                        <MaterialDeleteSvg />
                    </TemplateOptionActionIcon>
                </TemplateOptionDeleteIcon>
            )}
        </div>
    );
};

// Caption breve sotto l'opzione "Cropped print file", con link alla guida.
const OptionCaption = styled.div`
    margin-top: 4px;
    font-size: 12px;
    color: #71717a;
    line-height: 1.4;
`;

const ToolsRow = styled.div`
    display: grid;
    column-gap: 10px;
    grid-template-columns: 2fr 5fr auto;
    align-items: end;
    margin-bottom: 20px;
`;

const ToggleButtonsGroup = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0px, 1fr));
`;

// Wrapper della modale: il banner AI sta in cima, sopra il layout viewer + pannello destro.
const AreaDesignerRoot = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
`;

// Entry point AI nella toolbox: mostrato solo dopo che il banner in cima è stato chiuso.
const AiToggleButton = styled(AiIconButton)`
    padding: 7px;
    border-radius: 6px;
`;

const ToggleButton = styled.button<{ $selected: boolean }>`
    padding: 6px;
    border: 1px solid #e5e7eb;
    color: #71717a;
    background-color: white;
    transition: all 0.12s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    ${Icon} {
        width: 18px;
        height: 18px;
        min-width: 18px;
    }

    &:first-child {
        border-top-left-radius: 6px;
        border-bottom-left-radius: 6px;
    }

    &:last-child {
        border-top-right-radius: 6px;
        border-bottom-right-radius: 6px;
    }

    ${(props) =>
        props.disabled &&
        `
        background-color: #f9fafb;
		color: #d1d5db;
    `}

    ${(props) =>
        props.$selected &&
        `
        border-color: #263238;
        color: #263238;
        border-right-width: 1px;
    `}

    ${(props) =>
        !props.$selected &&
        `
		&:not(:last-child) {
			border-right-width: 0px;
		}
	`}

    ${(props) =>
        !props.disabled &&
        `
		&:hover {
			border-color: #263238;
			color: #263238;
			border-right-width: 1px;
			cursor: pointer;
		}
	`}
`;

const customSizeOption = {
    value: -1,
    isDefault: true,
    label: T._('Custom size...', 'Admin')
};

interface LayerColor {
    layerID: string;
    color: string;
}

export interface AreaDesignerProps {
    modelSide: ModelSide;
    modelColor: ModelColor;
    disabledModelSides: DisabledModelSide[];
    modelOptions: ModelOptions | null;
    onSaveClick: (modelOptions: ModelOptions | null, isDirty: boolean, areasWereDeleted: boolean, deletedAreasCount: number) => void;
    onModelSideChanged: (modelSide: ModelSide) => void;
    onDirtyChange?: (isDirty: boolean) => void;
    // Registra la funzione di salvataggio dell'editor (equivalente al click su "Apply") così che
    // il banner "unsaved changes" possa salvare dallo stesso flusso del pulsante Apply.
    onRegisterSave?: (save: () => void) => void;
}

const AreaDesignerContent: FC<AreaDesignerProps> = (props) => {
    const validation = useContext(validationContext);

    const viewerRef = useRef<{ resize: () => void; hideRuler: () => void; fitToBounds: () => void }>(null);
    const viewerContainerRef = useRef<HTMLDivElement>(null);
    const environment = useRelayEnvironment();

    const { showDialog, closeDialog } = useDialogManager();
    const [selectedItemGuid, setSelectedItemGuid] = useState<string | null | undefined>(
        props.modelSide.ppcm > 0 && props.modelSide.areas.length > 0 ? props.modelSide.areas[0].guid : null
    );
    const [width, setWidth] = useState('');
    const [height, setHeight] = useState('');
    const [isDirty, setIsDirty] = useState(false);
    const [name, setName] = useState('');
    const [templateId, setTemplateID] = useState<number | null>(null);
    const [unit, setUnit] = useState<Unit>(getStartingUnit());
    const [isLoadingPng, setIsLoadingPng] = useState(false);
    const [linkSize, setLinkSize] = useState(false);
    const [tool, setTool] = useState<Tool>(props.modelSide.ppcm > 0 ? 'pan' : 'ruler');
    const sizeInputDirty = useRef(false);
    const autoApplyTimerRef = useRef<number | null>(null);
    const pendingAutoApplyRef = useRef<(() => void) | null>(null);
    const AUTO_APPLY_DEBOUNCE_MS = 350;
    const latestModelSideRef = useRef(props.modelSide);
    latestModelSideRef.current = props.modelSide;

    const notifyModelSideChanged = (modelSide: ModelSide) => {
        latestModelSideRef.current = modelSide;
        props.onModelSideChanged(modelSide);
    };

    const flushAutoApply = () => {
        if (autoApplyTimerRef.current !== null) {
            clearTimeout(autoApplyTimerRef.current);
            autoApplyTimerRef.current = null;
        }
        const pending = pendingAutoApplyRef.current;
        pendingAutoApplyRef.current = null;
        if (pending) pending();
    };

    const [rulerInputVisible, setRulerInputVisible] = useState(false);
    const [rulerInputPosition, setRulerInputPosition] = useState([0, 0]);
    const [rulerLineLength, setRulerLineLength] = useState(0);
    const [autoGenerateBannerVisible, setAutoGenerateBannerVisible] = useState(true);

    const deletedAreasQueueRef = useRef<DeletedAreaIdentifier[]>([]);
    const deletedAreaIdsRef = useRef<Set<number>>(new Set());
    const [areasWereDeleted, setAreasWereDeleted] = useState(false);

    const enqueueDeletedArea = (id: DeletedAreaIdentifier) => {
        deletedAreasQueueRef.current = [...deletedAreasQueueRef.current, id];
        if (id.areaID) deletedAreaIdsRef.current.add(id.areaID);
        setAreasWereDeleted(true);
    };

    const dequeueDeletedArea = (): DeletedAreaIdentifier | undefined => {
        if (deletedAreasQueueRef.current.length === 0) return undefined;
        const [first, ...rest] = deletedAreasQueueRef.current;
        deletedAreasQueueRef.current = rest;
        return first;
    };

    const selectedArea = useMemo(
        () => (selectedItemGuid ? props.modelSide.areas.find((x) => x.guid === selectedItemGuid) : null),
        [selectedItemGuid, props.modelSide]
    );
    const toolsDisabled = tool === 'ruler' && props.modelSide.ppcm === 0;

    const [layersColors, setLayersColors] = useState<LayerColor[]>([]);
    const [openedLayers, setOpenedLayers] = useState<string[]>([]);
    const [editableModelOptions, setEditableModelOptions] = useState<EditableModelOptions>(
        props.modelOptions
            ? modelOptionsToEditableModelOptions(props.modelOptions, props.modelSide.ppcm, unit)
            : createDefaultEditableModelOptions(props.modelColor, null)
    );

    useEffect(() => {
        props.onDirtyChange?.(isDirty);
    }, [isDirty]);

    useEffect(() => {
        return () => {
            if (autoApplyTimerRef.current !== null) clearTimeout(autoApplyTimerRef.current);
            pendingAutoApplyRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (autoApplyTimerRef.current !== null) {
            clearTimeout(autoApplyTimerRef.current);
            autoApplyTimerRef.current = null;
        }
        pendingAutoApplyRef.current = null;
    }, [selectedItemGuid]);

    useEffect(() => {
        sizeInputDirty.current = false;
        updateAreaInfo();
    }, [unit, selectedItemGuid]);

    useEffect(() => {
        updateAreaInfo();
    }, [props.modelSide]);

    useEffect(() => {
        if (selectedArea) {
            const template = findTemplateBySize(parseFloat(width), parseFloat(height));
            setTemplateID(template?.templateID ?? -1);
        }
    }, [width, height]);

    useEffect(() => {
        if (selectedArea) {
            const template = findTemplateForArea(selectedArea);
            setTemplateID(template?.templateID ?? -1);

            updateLayersColors(selectedArea);
            setOpenedLayers([]);
        }
    }, [selectedItemGuid]);

    useLayoutEffect(() => {
        viewerRef.current?.resize();
    }, [templateId]);

    const printAreaTemplatesQuery = graphql`
        query areaDesignerQuery {
            printAreaTemplates {
                templateID
                isDefault
                name
                width
                height
            }
        }
    `;

    const query = useLazyLoadQuery<areaDesignerQuery>(printAreaTemplatesQuery, {});

    const isBleedLinesMode = props.modelSide.areas.some((x) => x.layers.length > 0);

    const [commiDeleteTemplate] = useMutation(graphql`
        mutation areaDesignerMutation($id: ID!) {
            deletePrintAreaTemplate(id: $id) {
                printAreaTemplate {
                    name
                }
            }
        }
    `);

    const [commitPredictPrintAreas, isPredictingArea] = useMutation(graphql`
        mutation areaDesignerPredictPrintAreasMutation($input: PredictPrintAreasInput!) {
            predictPrintAreas(input: $input) {
                success
                error
                imageInfo {
                    width
                    height
                    ppcmAvg
                }
                detections {
                    confidence
                    bboxPixels {
                        x
                        y
                        width
                        height
                    }
                }
            }
        }
    `);

    const templatesOptionsList = query.printAreaTemplates.map((p) => {
        const width = round(p.width / unit.ratio, 3);
        const height = round(p.height / unit.ratio, 3);
        const label = `${p.name} (${width} x ${height} ${unit.value})`;
        return { value: p.templateID, isDefault: p.isDefault, label: p.isDefault ? p.name : label };
    });

    const handleAreaCreated = (item: paper.Item) => {
        const area = createAreaFromPaperItem(item, dequeueDeletedArea());

        const modelSide = update(props.modelSide, {
            areas: { $push: [area] }
        });

        notifyModelSideChanged(modelSide);
        setSelectedItemGuid(area.guid);
        setTool('pan');
    };

    const handleConfirmAreaRemoved = (area: Area) => {
        //if is a old print-area, enqueue for reuse
        if (area.areaID) {
            enqueueDeletedArea({ areaID: area.areaID, guid: area.guid });
        }
        handleAreaRemoved(area);
    };

    const handleAreaChanged = (area: Area, newPPCM: number | null, layers: AreaLayer[]) => {
        if (selectedArea) {
            const dimensionsChanged =
                area.boundsWidth !== selectedArea.boundsWidth ||
                area.boundsHeight !== selectedArea.boundsHeight ||
                (newPPCM !== null && newPPCM !== props.modelSide.ppcm);

            const targetPpcm = newPPCM ?? props.modelSide.ppcm;
            const updatedModelSide = update(props.modelSide, {
                ppcm: {
                    $set: targetPpcm
                },
                areas: {
                    [props.modelSide.areas.indexOf(selectedArea)]: {
                        $merge: { ...area, layers } as Partial<ModelArea> // For some reason Area and ModelArea won't overlap
                    }
                }
            });

            notifyModelSideChanged(updatedModelSide);

            if (dimensionsChanged) {
                // Capture the snapped target values from the just-resized area, so the auto-apply
                // applies the freshly dragged dimensions without overwriting newer area changes.
                const snappedWidthCm = round(area.boundsWidth / targetPpcm / unit.ratio, 3) * unit.ratio;
                const snappedHeightCm = round(area.boundsHeight / targetPpcm / unit.ratio, 3) * unit.ratio;
                const targetGuid = area.guid;

                const runAutoApply = () => {
                    if (sizeInputDirty.current) return;
                    if (snappedWidthCm <= 0 || snappedHeightCm <= 0) return;
                    const latestModelSide = latestModelSideRef.current;
                    const targetArea = latestModelSide.areas.find((a) => a.guid === targetGuid);
                    if (!targetArea) return;
                    const snappedModelSide = update(latestModelSide, {
                        areas: {
                            [latestModelSide.areas.indexOf(targetArea)]: {
                                $set: resizeAreaInCM(targetArea, latestModelSide.ppcm, snappedWidthCm, snappedHeightCm, 'top')
                            }
                        }
                    });
                    notifyModelSideChanged(snappedModelSide);
                };

                if (autoApplyTimerRef.current !== null) clearTimeout(autoApplyTimerRef.current);
                pendingAutoApplyRef.current = runAutoApply;
                autoApplyTimerRef.current = window.setTimeout(() => {
                    autoApplyTimerRef.current = null;
                    pendingAutoApplyRef.current = null;
                    runAutoApply();
                }, AUTO_APPLY_DEBOUNCE_MS);
            }
        }
    };

    const handleAreaRemoved = (area: Area) => {
        const index = props.modelSide.areas.findIndex((modelArea) => modelArea.guid === area.guid);

        const newModelSide = update(props.modelSide, {
            ['areas']: { $splice: [[index, 1]] }
        });

        notifyModelSideChanged(newModelSide);

        if (area.guid == selectedItemGuid) setSelectedItemGuid(null);

        if (newModelSide.areas.length === 0) {
            setTool('rectangle');
        }
    };

    const handlePPCMChanged = (ppcm: number) => {
        let modelSide = _.clone(props.modelSide);
        modelSide.ppcm = ppcm;
        notifyModelSideChanged(modelSide);
    };

    const handleAddPngArea = () => {
        const input = document.createElement('input');
        input.style.display = 'none';
        input.setAttribute('type', 'file');
        input.addEventListener('change', (e) => {
            const reader = new FileReader();
            const files = (e.currentTarget as HTMLInputElement).files;
            const file = files && files.length > 0 ? files[0] : null;

            reader.addEventListener(
                'load',
                function () {
                    const image = new Image();
                    image.onload = () => {
                        findOutlines(image, (result) => {
                            if (!result.success) {
                                showErrorNotification({
                                    message:
                                        result.error === 'NO_TRANSPARENT_BORDER' ? (
                                            <>
                                                {T._('Image does not have a transparent border of at last 1px wide.', 'Admin') + ' '}
                                                <Link
                                                    target='_blank'
                                                    href='https://zakeke.zendesk.com/hc/en-us/articles/360009572080-Using-a-PNG-mask-to-create-a-print-area'
                                                >
                                                    {T._('Read more', 'Admin')}
                                                </Link>
                                            </>
                                        ) : (
                                            <>
                                                {T._('Error generating mask.', 'Admin') + ' '}
                                                <Link
                                                    target='_blank'
                                                    href='https://zakeke.zendesk.com/hc/en-us/articles/360009572080-Using-a-PNG-mask-to-create-a-print-area'
                                                >
                                                    {T._('Read more', 'Admin')}
                                                </Link>
                                            </>
                                        )
                                });
                                closeDialog('loading-png'); // Chiudi in caso di errore
                                setIsLoadingPng(false);
                                return;
                            }

                            addAreaFromOutlineResult(result);
                            closeDialog('loading-png'); // Chiudi dopo elaborazione riuscita
                            setIsLoadingPng(false);
                        });
                    };
                    image.setAttribute('src', reader.result as string);
                    // Rimosso setIsLoadingPng(false) da qui
                },
                false
            );

            if (file) {
                setIsLoadingPng(true);
                showDialog(
                    'loading-png',
                    <Dialog>
                        <UploadProgressRow label={T._('Processing PNG mask...', 'Admin')} status='PROCESSING' progress={0} />
                    </Dialog>
                );
                reader.readAsDataURL(file);
            }
        });
        document.body.appendChild(input);
        input.click();
        setTimeout(() => input.remove(), 10000);
    };

    // Add bleed lines PDF
    const handleAddPdf = () => {
        openFileDialog(
            async (files) => {
                if (files.length > 0) {
                    const fileID = uuid();

                    showDialog(
                        'upload-pdf',
                        <LayersUploadDialog
                            fileID={fileID}
                            file={files[0]}
                            onComplete={(areaTemplateInfo) => {
                                closeDialog('upload-pdf');

                                if (areaTemplateInfo.layers.length == 0) {
                                    showErrorNotification({
                                        message: T._('The PDF must contain at least one layer.', 'Admin')
                                    });

                                    return;
                                }

                                // Enqueue existing areas with areaID for reuse before replacing
                                props.modelSide.areas.filter((a) => a.areaID !== null).forEach((a) => enqueueDeletedArea({ areaID: a.areaID!, guid: a.guid }));

                                // Reuse only areaID, not guid: a fresh guid forces setAreas' remove+create path so the layers plugin re-imports bleed paths.
                                const reused = dequeueDeletedArea();
                                const area = createAreaFromSizeInCM(areaTemplateInfo.sizeCm.width, areaTemplateInfo.sizeCm.height, props.modelSide.ppcm);
                                if (reused) area.areaID = reused.areaID;

                                // Put the file id to template file
                                area.templateFile = {
                                    fileID,
                                    fileObjectID: null,
                                    documentWidth: areaTemplateInfo.sizeCm.width,
                                    documentHeight: areaTemplateInfo.sizeCm.height
                                };

                                // Attach template layers to area
                                area.layers = areaTemplateInfo.layers.map((x, index) => ({
                                    ...createAreaLayerFromTemplateLayer(
                                        x,
                                        area,
                                        areaTemplateInfo.sizeCm.width,
                                        areaTemplateInfo.sizeCm.height,
                                        props.modelSide.ppcm
                                    ),
                                    displayOrder: index
                                }));

                                // Remove all print areas and remove the rotation
                                const modelSide = update(props.modelSide, {
                                    areas: {
                                        $set: [area]
                                    },
                                    canRotate: {
                                        $set: false
                                    }
                                });

                                if (selectedArea && selectedArea?.areaID != area.areaID) {
                                    showNotification({
                                        type: 'warning',
                                        message: stringFormat(
                                            T._(
                                                'A print area was updated. Please note that if you have pre-designed templates built on this product, they are no longer working. You need to adjust them to the new print area.',
                                                'Admin'
                                            )
                                        ),
                                        dismiss: {
                                            duration: 10000
                                        }
                                    });
                                }
                                notifyModelSideChanged(modelSide);
                                setSelectedItemGuid(area.guid);
                                setTool('pan');
                            }}
                        />
                    );
                }
            },
            '.pdf',
            false
        );
    };

    const handleCustomSizeChange = () => {
        const parsedWidth = parseFloat(width.replace(',', '.'));
        const parsedHeight = parseFloat(height.replace(',', '.'));

        if (!validation.submit()) return;

        if (selectedArea) {
            sizeInputDirty.current = false;
            resizeCurrentArea(parsedWidth * unit.ratio, parsedHeight * unit.ratio);
            setIsDirty(false);
        }
    };

    const handleClipOutChange = (checked: boolean) => {
        if (selectedArea) {
            const modelSide = update(props.modelSide, {
                areas: {
                    [props.modelSide.areas.indexOf(selectedArea)]: {
                        ['clipOut']: { $set: checked }
                    }
                }
            });

            notifyModelSideChanged(modelSide);
        }
    };

    const handleTemplateSizeChange = (
        selectedValue: SingleValue<(typeof templatesOptionsList)[0]>,
        actionMeta: ActionMeta<(typeof templatesOptionsList)[0]>
    ) => {
        if (actionMeta.action === 'remove-value') {
            if (selectedValue) deletePrintAreaTemplate(selectedValue.value);
        } else {
            setTemplateID(selectedValue ? selectedValue.value : null);

            if (selectedValue && selectedValue.value != -1) {
                const printAreaTemplate = query.printAreaTemplates.find((x) => x.templateID == selectedValue.value);
                if (printAreaTemplate) {
                    resizeCurrentArea(printAreaTemplate.width, printAreaTemplate.height);
                    setWidth(round(printAreaTemplate.width / unit.ratio, 3).toString());
                    setHeight(round(printAreaTemplate.height / unit.ratio, 3).toString());
                }
            }
        }
    };

    const handleRulerInputConfirm = (size: number) => {
        const cm = size * unit.ratio;

        const modelSide = update(props.modelSide, {
            ['ppcm']: {
                $set: rulerLineLength / cm
            }
        });

        if (props.modelSide.ppcm === 0) setTool('rectangle');

        viewerRef.current?.hideRuler();
        notifyModelSideChanged(modelSide);
    };

    const refreshPrintAreaTemplates = () => {
        fetchQuery(environment, printAreaTemplatesQuery, {}).toPromise();
    };

    const deletePrintAreaTemplate = (templateIdToDelete: number) => {
        showDialog(
            'remove-template',
            <QuestionDialog
                destructive
                message={T._('Do you want remove the selected size?', 'Admin')}
                onYesClick={() => {
                    closeDialog('remove-template');
                    commiDeleteTemplate({
                        updater: (store) => {
                            if (templateIdToDelete === templateId) setTemplateID(-1);

                            store.invalidateStore();
                            refreshPrintAreaTemplates();
                        },
                        variables: {
                            id: 'guid://Zakeke/PrintAreaTemplate/' + templateIdToDelete
                        }
                    });
                }}
            />
        );
    };

    const resizeCurrentArea = (width: number, height: number) => {
        if (selectedArea) {
            const modelSide = update(props.modelSide, {
                areas: {
                    [props.modelSide.areas.indexOf(selectedArea)]: {
                        $set: resizeAreaInCM(selectedArea, props.modelSide.ppcm, width, height, 'top')
                    }
                }
            });

            notifyModelSideChanged(modelSide);
        }
    };

    const findTemplateBySize = (width: number, height: number, unit?: Unit) => {
        return query.printAreaTemplates.find(
            (x) =>
                Math.abs(x.width - (unit != null ? width * unit?.ratio : width)) < 0.01 &&
                Math.abs(x.height - (unit != null ? height * unit?.ratio : height)) < 0.01
        );
    };

    const findTemplateForArea = (area: Area) => {
        const areaWidthCm = area.boundsWidth / props.modelSide.ppcm;
        const areaHeightCm = area.boundsHeight / props.modelSide.ppcm;
        return findTemplateBySize(areaWidthCm, areaHeightCm);
    };

    const updateLayersColors = async (area: Area) => {
        for (const layer of area.layers) {
            let index = layersColors.findIndex((x) => x.layerID === layer.id);
            if (index === -1) {
                const layerColor = {
                    layerID: layer.id,
                    color: getLayerColor(layer)
                };

                setLayersColors((prev) => [...prev, layerColor]);
            } else {
                setLayersColors((prev) =>
                    update(prev, {
                        [index]: {
                            ['color']: {
                                $set: getLayerColor(layer)
                            }
                        }
                    })
                );
            }
        }
    };

    const updateAreaInfo = () => {
        const area = props.modelSide.areas.find((x) => x.guid == selectedItemGuid);
        if (area) {
            if (!sizeInputDirty.current) {
                setWidth(round(area.boundsWidth / props.modelSide.ppcm / unit.ratio, 3).toString());
                setHeight(round(area.boundsHeight / props.modelSide.ppcm / unit.ratio, 3).toString());
            }
            setName(area.name);
        } else {
            sizeInputDirty.current = false;
            setWidth('');
            setHeight('');
            setName('');
            setTemplateID(null);
        }
    };

    const setAreaName = (name: string) => {
        if (selectedItemGuid) {
            let area = props.modelSide.areas.find((x) => x.guid == selectedItemGuid)!;
            const index = props.modelSide.areas.indexOf(area);

            area = update(area, { $merge: { name } });
            const modelSide = update(props.modelSide, {
                areas: {
                    [index]: {
                        $set: area
                    }
                }
            });

            notifyModelSideChanged(modelSide);
        }
    };

    const addAreaFromOutlineResult = (result: { outlines: Outline[]; width: number; height: number }) => {
        if (result.outlines.length == 0) {
            showDialog('no-shapes', <MessageDialog message={T._('No shapes found in the image.', 'Admin')} />);
            return;
        }

        // Get side image width and height
        const image = new Image();
        image.onload = () => {
            const area = createAreaFromOutlinesResult(result, image.width, image.height, dequeueDeletedArea());

            const modelSide = update(props.modelSide, {
                areas: { $push: [area] }
            });

            notifyModelSideChanged(modelSide);
            setSelectedItemGuid(area.guid);
            setTool('pan');
        };
        image.src = props.modelSide.imageUrl;
    };

    // Generate the print area through the AI Print Area Predictor service: send the side image, take the
    // detection with the highest confidence, and create an area positioned on its bounding box. The area
    // stays editable with the manual tools afterwards.
    const handleGenerateAreaWithAI = () => {
        if (!props.modelSide.imageUrl || isPredictingArea) return;

        showDialog(
            'ai-generating-area',
            <Dialog>
                <AIGeneratingIndicator
                    phrases={['Analyzing your product image...', 'Detecting the best print area...', 'Positioning the print area...', 'Almost there...']}
                />
            </Dialog>
        );

        commitPredictPrintAreas({
            variables: { input: { imageUrl: props.modelSide.imageUrl, includeAnnotatedImage: false } },
            onCompleted: (response: any) => {
                closeDialog('ai-generating-area');

                const result = response?.predictPrintAreas;
                if (!result?.success) {
                    showErrorNotification({ message: result?.error || T._('The print area could not be generated. Please try again.', 'Admin') });
                    return;
                }

                const detections = result.detections ?? [];
                const imageWidth = result.imageInfo?.width;
                const imageHeight = result.imageInfo?.height;

                let area: ModelArea;
                if (detections.length > 0) {
                    // Highest-confidence detection.
                    const best = detections.reduce((a: any, b: any) => (b.confidence > a.confidence ? b : a));
                    const box = best.bboxPixels;
                    const imgW = imageWidth || box.width;
                    const imgH = imageHeight || box.height;

                    // Areas live in a space whose origin is the center of the side image: translate the bounding box
                    // (top-left origin, in image pixels) relative to that center.
                    area = createAreaFromBoundsPX(box.x - imgW / 2, box.y - imgH / 2, box.width, box.height, dequeueDeletedArea());
                } else if (imageWidth && imageHeight) {
                    // No detection: fall back to a centered default area so generation always yields at least one editable area.
                    area = createDefaultCenteredArea(imageWidth, imageHeight, dequeueDeletedArea());
                } else {
                    showWarningNotification({
                        message: T._("Oops... It wasn't possible to automatically generate the print area. Please set it manually.", 'Admin')
                    });
                    return;
                }

                // Use the PPCM estimated by the predictor so the area gets a real physical size (important on
                // sides that have not been measured yet).
                const predictedPpcm = result.imageInfo?.ppcmAvg;

                notifyModelSideChanged(
                    update(props.modelSide, {
                        ...(predictedPpcm && predictedPpcm > 0 ? { ppcm: { $set: predictedPpcm } } : {}),
                        areas: { $push: [area] }
                    })
                );
                setSelectedItemGuid(area.guid);
                setTool('pan');
            },
            onError: () => {
                closeDialog('ai-generating-area');
                showErrorNotification({ message: T._('The print area could not be generated. Please try again.', 'Admin') });
            }
        });
    };

    const handleLayerHeaderClick = (layerID: string) => {
        if (openedLayers.indexOf(layerID) !== -1) setOpenedLayers(openedLayers.filter((x) => x !== layerID));
        else setOpenedLayers([...openedLayers, layerID]);
    };

    const handleLayerPropChange = (layerID: string, key: keyof ModelAreaLayer, value: ModelAreaLayer[keyof ModelAreaLayer]) => {
        if (selectedArea) {
            let area = selectedArea;
            const areaIndex = props.modelSide.areas.indexOf(area);
            const layerIndex = area.layers.findIndex((x) => x.id === layerID);

            area = update(area, {
                ['layers']: {
                    [layerIndex]: {
                        [key]: {
                            $set: value
                        }
                    }
                }
            });

            notifyModelSideChanged(
                update(props.modelSide, {
                    ['areas']: {
                        [areaIndex]: {
                            $set: area
                        }
                    }
                })
            );
        }
    };

    const handleVisibleInCartChange = (checked: boolean) => {
        if (selectedArea) {
            let area = selectedArea;
            const areaIndex = props.modelSide.areas.indexOf(area);

            area = update(area, {
                ['layers']: {
                    $set: area.layers.map((x) => ({ ...x, isVisibleInCart: checked }))
                }
            });

            notifyModelSideChanged(
                update(props.modelSide, {
                    ['areas']: {
                        [areaIndex]: {
                            $set: area
                        }
                    }
                })
            );
        }
    };

    const handleVisibleInOutputChange = (checked: boolean) => {
        if (selectedArea) {
            let area = selectedArea;
            const areaIndex = props.modelSide.areas.indexOf(area);

            area = update(area, {
                ['layers']: {
                    $set: area.layers.map((x) => ({ ...x, isVisibleInOutput: checked }))
                }
            });

            notifyModelSideChanged(
                update(props.modelSide, {
                    ['areas']: {
                        [areaIndex]: {
                            $set: area
                        }
                    }
                })
            );
        }
    };

    const handleClipMaskChange = (layerID: string) => {
        if (selectedArea) {
            let area = selectedArea;
            const areaIndex = props.modelSide.areas.indexOf(area);
            const layer = area.layers.find((x) => x.id === layerID)!;
            let newArea!: ModelArea;

            if (!layer.isClipMask) {
                // If this layer will be the new clip mask
                newArea = createAreaFromModelAreaLayer(layer, props.modelSide.ppcm, area.templateFile!.documentWidth, area.templateFile!.documentHeight);
            } else {
                // If we are de-selecting the clip mask then return to the old area
                newArea = createAreaFromSizeInCM(area.templateFile!.documentWidth, area.templateFile!.documentHeight, props.modelSide.ppcm);
            }

            area = update(area, {
                $merge: newArea,
                ['areaID']: {
                    $set: area.areaID
                },
                ['guid']: {
                    $set: area.guid
                },
                ['templateFile']: {
                    $set: area.templateFile
                },
                ['layers']: {
                    $set: area.layers.map((x) => ({
                        ...x,
                        isClipMask: x.isClipMask ? false : x.id === layerID
                    }))
                }
            });

            notifyModelSideChanged(
                update(props.modelSide, {
                    ['areas']: {
                        [areaIndex]: {
                            $set: area
                        }
                    }
                })
            );
        }
    };

    // Salvataggio dell'editor: stessa logica del pulsante "Apply". Estratto in una funzione così da
    // poterlo invocare anche dal banner "unsaved changes".
    const handleSave = () => {
        if (!validation.submit()) return;

        // Apply any pending canvas-resize snap synchronously so the save uses the snapped bounds.
        flushAutoApply();

        // If there are pending dimension changes in the inputs, apply them to the modelSide before saving
        if (sizeInputDirty.current && selectedArea && width !== '' && height !== '') {
            const areaIndex = props.modelSide.areas.indexOf(selectedArea);
            const updatedModelSide = update(props.modelSide, {
                areas: {
                    [areaIndex]: {
                        $set: resizeAreaInCM(selectedArea, props.modelSide.ppcm, parseFloat(width) * unit.ratio, parseFloat(height) * unit.ratio, 'top')
                    }
                }
            });
            notifyModelSideChanged(updatedModelSide);
        }

        props.onSaveClick(
            editableModelOptionsToModelOptions(editableModelOptions, props.modelSide.ppcm, unit),
            isDirty,
            areasWereDeleted,
            deletedAreaIdsRef.current.size
        );
    };

    // Registra un wrapper stabile che invoca sempre l'ultima versione di handleSave.
    const saveHandlerRef = useRef<() => void>(() => {});
    saveHandlerRef.current = handleSave;
    useEffect(() => {
        props.onRegisterSave?.(() => saveHandlerRef.current());
    }, []);

    return (
        <AreaDesignerRoot>
            {!isBleedLinesMode && autoGenerateBannerVisible && (
                <AutoGenerateBannerRow>
                    <AIGradientBanner
                        title={T._('Generate area with AI', 'Admin')}
                        subtitle={T._('Draw the print area with the tools, or let AI do it for you.', 'Admin')}
                        onClick={handleGenerateAreaWithAI}
                        onClose={() => setAutoGenerateBannerVisible(false)}
                    />
                </AutoGenerateBannerRow>
            )}
            <AreaDesignerLayout>
                <AreaDesignerViewerContainer ref={viewerContainerRef}>
                    <AreaDesignerViewer
                        ref={viewerRef}
                        unit={unit}
                        selectedItemGuid={selectedItemGuid}
                        sideImageUrl={props.modelSide.imageUrl}
                        areas={props.modelSide.areas}
                        ppcm={props.modelSide.ppcm}
                        tool={tool}
                        onAreaCreated={handleAreaCreated}
                        onAreaChanged={handleAreaChanged}
                        // onAreaRemoved={handleAreaRemoved}
                        onAreaRemoved={handleConfirmAreaRemoved}
                        onAreaSelected={(area) => setSelectedItemGuid(area?.guid)}
                        onPPCMChanged={handlePPCMChanged}
                        onShowRulerInput={(length, x, y) => {
                            if (length < 20) {
                                showNotification({
                                    type: 'warning',
                                    message: T._('Please click and drag the mouse to measure the product.', 'Admin'),
                                    dismiss: {
                                        duration: 5000
                                    }
                                });

                                viewerRef.current?.hideRuler();
                                return;
                            }

                            const viewerWidth = viewerContainerRef.current?.offsetWidth ?? 0;
                            const viewerHeight = viewerContainerRef.current?.offsetHeight ?? 0;

                            // Invert if near border
                            x = x + 250 > viewerWidth ? x - 240 : x + 10;
                            y = y + 180 > viewerHeight ? y - 170 : y + 10;

                            // Don't exceed borders
                            x = Math.min(x, viewerWidth - 240);
                            y = Math.min(y, viewerHeight - 170);

                            setRulerInputVisible(true);
                            setRulerInputPosition([x, y]);
                            setRulerLineLength(length);
                        }}
                        onHideRulerInput={() => setRulerInputVisible(false)}
                    />

                    {rulerInputVisible && (
                        <RulerInput
                            x={rulerInputPosition[0]}
                            y={rulerInputPosition[1]}
                            unit={unit}
                            size={props.modelSide.ppcm > 0 ? rulerLineLength / props.modelSide.ppcm / unit.ratio : 0}
                            onConfirm={handleRulerInputConfirm}
                        />
                    )}
                </AreaDesignerViewerContainer>

                <AreaDesignerRightPanel>
                    <Stack>
                        <FormControl
                            label={T._('Measure unit', 'Admin')}
                            tooltipLabelHtml={
                                <span>
                                    {T._(
                                        'The unit of measure selected here only affects the configuration of this tool. It does not determine what the end user will see in the interface. The unit of measure displayed to users is defined as a global setting in Settings > User Interface Settings > Unit of Measure.',
                                        'Admin'
                                    )}
                                </span>
                            }
                        >
                            <AdvancedSelect
                                isSearchable={false}
                                options={units}
                                placeholder={T._('Select unit...', 'Admin')}
                                onChange={(e) => {
                                    const currentUnit = unit;
                                    const newUnit = e as (typeof units)[number];
                                    setUnit(newUnit);

                                    // Convert all current resizable options to the new unit
                                    if (editableModelOptions) {
                                        let newEditableModelOptions: EditableModelOptions = {
                                            ...editableModelOptions,
                                            startWidth: editableModelOptions.startWidth
                                                ? (
                                                      Math.round(((parseFloat(editableModelOptions.startWidth) * currentUnit.ratio) / newUnit.ratio) * 100) /
                                                      100
                                                  ).toString()
                                                : '',
                                            startHeight: editableModelOptions.startHeight
                                                ? (
                                                      Math.round(((parseFloat(editableModelOptions.startHeight) * currentUnit.ratio) / newUnit.ratio) * 100) /
                                                      100
                                                  ).toString()
                                                : '',
                                            minWidth: editableModelOptions.minWidth
                                                ? (
                                                      Math.round(((parseFloat(editableModelOptions.minWidth) * currentUnit.ratio) / newUnit.ratio) * 100) / 100
                                                  ).toString()
                                                : '',
                                            minHeight: editableModelOptions.minHeight
                                                ? (
                                                      Math.round(((parseFloat(editableModelOptions.minHeight) * currentUnit.ratio) / newUnit.ratio) * 100) / 100
                                                  ).toString()
                                                : '',
                                            maxWidth: editableModelOptions.maxWidth
                                                ? (
                                                      Math.round(((parseFloat(editableModelOptions.maxWidth) * currentUnit.ratio) / newUnit.ratio) * 100) / 100
                                                  ).toString()
                                                : '',
                                            maxHeight: editableModelOptions.maxHeight
                                                ? (
                                                      Math.round(((parseFloat(editableModelOptions.maxHeight) * currentUnit.ratio) / newUnit.ratio) * 100) / 100
                                                  ).toString()
                                                : '',
                                            depth: editableModelOptions.depth
                                                ? (
                                                      Math.round(((parseFloat(editableModelOptions.depth) * currentUnit.ratio) / newUnit.ratio) * 100) / 100
                                                  ).toString()
                                                : ''
                                        };

                                        setEditableModelOptions(newEditableModelOptions);
                                    }

                                    try {
                                        localStorage.setItem('area_designer_settings', JSON.stringify({ unit: newUnit.value }));
                                    } catch {}
                                }}
                                isMulti={false}
                                styles={{
                                    menu: (prev) => ({ ...prev, zIndex: 3 }) as CSSObjectWithLabel // Move on top of the size select box
                                }}
                                value={unit}
                            />
                        </FormControl>

                        <FormControl label={T._('Name', 'Admin')}>
                            <Input
                                disabled={!selectedItemGuid}
                                onChange={(e) => {
                                    setName(e.currentTarget.value);
                                    setAreaName(e.currentTarget.value);
                                }}
                                onBlurCapture={(e) => setAreaName(e.currentTarget.value)}
                                value={name}
                                validationKey={'validationPrintAreaName'}
                                validate={(value) => validateInputSecurity(value).isValid == false}
                                message={`${
                                    !validateInputSecurity(name).isValid
                                        ? T._(`Invalid characters detected: ${validateInputSecurity(name).invalidCharacters.join(', ')}`, 'Admin')
                                        : ''
                                }`}
                            />
                        </FormControl>
                        {/* Border flag */}
                        {!isBleedLinesMode && (
                            <FormControl label={T._('Size', 'Admin')}>
                                <AdvancedSelect<(typeof templatesOptionsList)[number]>
                                    menuPosition={'fixed'}
                                    isSearchable={false}
                                    isDisabled={!selectedItemGuid}
                                    options={templatesOptionsList}
                                    placeholder={T._('Select size...', 'Admin')}
                                    onChange={handleTemplateSizeChange}
                                    value={
                                        templateId
                                            ? templateId === -1
                                                ? customSizeOption
                                                : templatesOptionsList.find(({ value }) => value == templateId)
                                            : null
                                    }
                                    components={{ Option: TemplateOption }}
                                />
                            </FormControl>
                        )}

                        <SizeColumns>
                            <FormControl label={T._('Width', 'Admin') + ` (${unit.value})`}>
                                <Input
                                    disabled={!selectedItemGuid}
                                    type='text'
                                    inputMode='decimal'
                                    value={width}
                                    validationKey='area-designer-width'
                                    validate={(value) => {
                                        if (!selectedItemGuid) return false;
                                        const parsedValue = parseFloat(value.toString().replace(',', '.'));
                                        return !parsedValue || parsedValue <= 0;
                                    }}
                                    validationMessage={T._('Width must be greater than zero.', 'Admin')}
                                    onKeyDown={(e) => {
                                        if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
                                            e.stopPropagation();
                                            return;
                                        }
                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            sizeInputDirty.current = true;
                                            setWidth((prev) => {
                                                setIsDirty(true);
                                                const num = parseFloat(prev.replace(',', '.')) || 0;
                                                const step = 0.001;
                                                const newVal = e.key === 'ArrowUp' ? num + step : num - step;
                                                return newVal.toFixed(3);
                                            });
                                        }
                                    }}
                                    onChange={(e) => {
                                        setIsDirty(true);
                                        const value = e.currentTarget.value;

                                        if (/^[0-9.,]*$/.test(value)) {
                                            sizeInputDirty.current = true;
                                            setWidth(value);
                                        }

                                        if (linkSize && value !== '' && height !== '') {
                                            const ratio = parseFloat(width) / parseFloat(height);
                                            const newHeight = parseFloat(value.replace(',', '.')) / ratio;
                                            setHeight(newHeight.toFixed(3));
                                        }
                                    }}
                                />
                            </FormControl>
                            <LinkIcon active={linkSize} onClick={() => setLinkSize(!linkSize)}>
                                <MdLink />
                            </LinkIcon>
                            <FormControl label={T._('Height', 'Admin') + ` (${unit.value})`}>
                                <Input
                                    disabled={!selectedItemGuid}
                                    type='text'
                                    inputMode='decimal'
                                    value={height}
                                    validationKey='area-designer-height'
                                    validate={(value) => {
                                        if (!selectedItemGuid) return false;
                                        const parsedValue = parseFloat(value.toString().replace(',', '.'));
                                        return !parsedValue || parsedValue <= 0;
                                    }}
                                    validationMessage={T._('Height must be greater than zero.', 'Admin')}
                                    onKeyDown={(e) => {
                                        if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
                                            e.stopPropagation();
                                            return;
                                        }
                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            sizeInputDirty.current = true;
                                            setHeight((prev) => {
                                                setIsDirty(true);
                                                const num = parseFloat(prev.replace(',', '.')) || 0;
                                                const step = 0.001;
                                                const newVal = e.key === 'ArrowUp' ? num + step : num - step;
                                                return newVal.toFixed(3);
                                            });
                                        }
                                    }}
                                    onChange={(e) => {
                                        setIsDirty(true);
                                        const value = e.currentTarget.value;

                                        if (/^[0-9.,]*$/.test(value)) {
                                            sizeInputDirty.current = true;
                                            setHeight(value);
                                        }

                                        if (linkSize && value !== '' && width !== '') {
                                            const ratio = parseFloat(width) / parseFloat(height);
                                            const newWidth = parseFloat(value.replace(',', '.')) / ratio;
                                            setWidth(newWidth.toFixed(3));
                                        }
                                    }}
                                />
                            </FormControl>
                            <div className='apply-btn'>
                                <TextButton disabled={!selectedItemGuid || !isDirty} onClick={handleCustomSizeChange}>
                                    {T._('Apply', 'Admin')}
                                </TextButton>
                            </div>
                        </SizeColumns>

                        {!isBleedLinesMode && (
                            <ToolsRow>
                                <FormControl label={T._('Tools', 'Admin')}>
                                    <ToggleButtonsGroup>
                                        <ZTooltip position={'bottom'} title={T._('Edit areas or move/zoom view', 'Admin')}>
                                            <ToggleButton
                                                $selected={tool === 'pan'}
                                                disabled={toolsDisabled}
                                                onClick={() => {
                                                    setTool('pan');
                                                    setSelectedItemGuid(null);
                                                }}
                                            >
                                                <Icon>
                                                    <MdPanTool />
                                                </Icon>
                                            </ToggleButton>
                                        </ZTooltip>
                                        <ZTooltip position={'bottom'} title={T._('Indicate the product size', 'Admin')}>
                                            <ToggleButton
                                                $selected={tool === 'ruler'}
                                                disabled={false}
                                                onClick={() => {
                                                    setTool('ruler');
                                                    setSelectedItemGuid(null);
                                                }}
                                            >
                                                <Icon>
                                                    <MdStraighten />
                                                </Icon>
                                            </ToggleButton>
                                        </ZTooltip>
                                    </ToggleButtonsGroup>
                                </FormControl>
                                <FormControl label={T._('Shapes', 'Admin')}>
                                    <ToggleButtonsGroup>
                                        <ZTooltip position={'bottom'} title={T._('Create a rectangular area', 'Admin')}>
                                            <ToggleButton
                                                $selected={tool === 'rectangle'}
                                                disabled={toolsDisabled}
                                                onClick={() => {
                                                    setTool('rectangle');
                                                    setSelectedItemGuid(null);
                                                }}
                                            >
                                                <Icon>
                                                    <MdCropPortrait />
                                                </Icon>
                                            </ToggleButton>
                                        </ZTooltip>
                                        <ZTooltip position={'bottom'} title={T._('Create a circular area', 'Admin')}>
                                            <ToggleButton
                                                $selected={tool === 'circle'}
                                                disabled={toolsDisabled}
                                                onClick={() => {
                                                    setTool('circle');
                                                    setSelectedItemGuid(null);
                                                }}
                                            >
                                                <Icon>
                                                    <MdRadioButtonUnchecked />
                                                </Icon>
                                            </ToggleButton>
                                        </ZTooltip>
                                        <ZTooltip position={'bottom'} title={T._('Create a polygonal area', 'Admin')}>
                                            <ToggleButton
                                                $selected={tool === 'polygon'}
                                                disabled={toolsDisabled}
                                                onClick={() => {
                                                    setTool('polygon');
                                                    setSelectedItemGuid(null);
                                                }}
                                            >
                                                <Icon>
                                                    <MdTimeline />
                                                </Icon>
                                            </ToggleButton>
                                        </ZTooltip>
                                        <ZTooltip
                                            position={'bottom'}
                                            interactive={true}
                                            html={
                                                <Link
                                                    target={'_blank'}
                                                    href={'https://zakeke.zendesk.com/hc/en-us/articles/360009572080-Using-a-PNG-mask-to-create-a-print-area'}
                                                >
                                                    {T._('Use a PNG mask to create the print area', 'Admin')}
                                                </Link>
                                            }
                                        >
                                            <ToggleButton $selected={false} disabled={toolsDisabled} onClick={handleAddPngArea}>
                                                <Icon>
                                                    <MdStar />
                                                </Icon>
                                            </ToggleButton>
                                        </ZTooltip>
                                        <ZTooltip
                                            position={'bottom'}
                                            interactive={true}
                                            title={
                                                props.modelSide.canRotate
                                                    ? T._(
                                                          'This option is unavailable because the Side Rotation option has already been activated on this side, and they are not compatible. ',
                                                          'Admin'
                                                      )
                                                    : ''
                                            }
                                            html={
                                                props.modelSide.canRotate ? (
                                                    <Link
                                                        target='_blank'
                                                        href='https://zakeke.zendesk.com/hc/en-us/articles/11739151453468-Side-rotation-in-UI#h_01HHHFRA9VD4Y3BY39E6ZVQFMT'
                                                    >
                                                        {T._('Learn more', 'Admin')}
                                                    </Link>
                                                ) : (
                                                    <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/4403442390930'>
                                                        {T._('Use PDF to create bleed lines ', 'Admin')}
                                                    </Link>
                                                )
                                            }
                                        >
                                            <ToggleButton $selected={false} disabled={toolsDisabled || props.modelSide.canRotate} onClick={handleAddPdf}>
                                                <Icon>
                                                    <PDFIcon />
                                                </Icon>
                                            </ToggleButton>
                                        </ZTooltip>
                                    </ToggleButtonsGroup>
                                </FormControl>
                                {!autoGenerateBannerVisible && (
                                    <FormControl label={T._('AI', 'Admin')}>
                                        <ZTooltip
                                            position={'bottom'}
                                            html={
                                                <>
                                                    {T._(
                                                        'Automatically generates the print area based on the product type. The AI analyzes your product image and suggests the optimal customizable area.',
                                                        'Admin'
                                                    )}
                                                </>
                                            }
                                        >
                                            <AiToggleButton
                                                type='button'
                                                disabled={!props.modelSide.imageUrl || isPredictingArea}
                                                onClick={handleGenerateAreaWithAI}
                                            >
                                                <Sparkles />
                                            </AiToggleButton>
                                        </ZTooltip>
                                    </FormControl>
                                )}
                            </ToolsRow>
                        )}

                        {!isBleedLinesMode && (
                            <>
                                <FormControl>
                                    <Checkbox
                                        disabled={selectedArea === null}
                                        label={
                                            <>
                                                <span>{T._('Cropped output', 'Admin')}</span>
                                            </>
                                        }
                                        checked={selectedArea?.clipOut ?? false}
                                        onChange={handleClipOutChange}
                                    />
                                    <OptionCaption>
                                        {T._('Removes the parts of the design that exceed the print-area limits.', 'Admin')}{' '}
                                        <Link
                                            href='https://zakeke.zendesk.com/hc/en-us/articles/360016879139-How-to-define-a-Print-Area#h_01KX36MEY5NCPNNBHZD4F4GXB9'
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            {T._('Learn more', 'Admin')}
                                        </Link>
                                    </OptionCaption>
                                </FormControl>
                            </>
                        )}

                        {isBleedLinesMode && selectedArea && (
                            <LayersList>
                                {selectedArea.layers.map((x) => {
                                    const opened = openedLayers.indexOf(x.id) !== -1;

                                    return (
                                        <Layer key={x.id} opened={opened}>
                                            <LayerHeader onClick={() => handleLayerHeaderClick(x.id)}>
                                                <LayerColorBox color={layersColors.find((c) => c.layerID === x.id)?.color ?? '#000000'} />
                                                <span>{x.displayName}</span>
                                                {opened ? (
                                                    <Icon>
                                                        <UpArrowIcon />
                                                    </Icon>
                                                ) : (
                                                    <Icon>
                                                        <DownArrowIcon />
                                                    </Icon>
                                                )}
                                            </LayerHeader>
                                            <LayerContent>
                                                <Stack>
                                                    <FormControl label={T._('Layer name', 'Admin')}>
                                                        <Input
                                                            type='text'
                                                            value={x.displayName}
                                                            onChange={(e) => handleLayerPropChange(x.id, 'displayName', e.currentTarget.value)}
                                                        />
                                                    </FormControl>

                                                    <FormControl label={T._('Description', 'Admin')}>
                                                        <TextArea
                                                            value={x.description}
                                                            onChange={(e) => handleLayerPropChange(x.id, 'description', e.currentTarget.value)}
                                                        />
                                                    </FormControl>
                                                </Stack>

                                                <Checkbox
                                                    checked={x.isVisibleInCanvas}
                                                    label={T._('Visible to the end-users', 'Admin')}
                                                    onChange={(checked) => handleLayerPropChange(x.id, 'isVisibleInCanvas', checked)}
                                                />
                                                <Checkbox
                                                    checked={x.isVisibleInOutput}
                                                    label={T._('Include in the print-ready file', 'Admin')}
                                                    onChange={(checked) => handleLayerPropChange(x.id, 'isVisibleInOutput', checked)}
                                                />
                                                <Checkbox
                                                    checked={x.showLabel}
                                                    label={T._('Show names to the end-users', 'Admin')}
                                                    onChange={(checked) => handleLayerPropChange(x.id, 'showLabel', checked)}
                                                />
                                                <Checkbox
                                                    checked={x.showIn3DPreview}
                                                    label={T._('Visible in 3D preview', 'Admin')}
                                                    onChange={(checked) => handleLayerPropChange(x.id, 'showIn3DPreview', checked)}
                                                />

                                                <Checkbox
                                                    checked={x.isClipMask}
                                                    label={T._('Clip mask', 'Admin')}
                                                    onChange={(checked) => handleClipMaskChange(x.id)}
                                                    helpTooltip={T._(
                                                        `If checked, this line becomes a clipping area that shapes mask the design. Print-ready files won't be affected as it will be the same shape of the uploaded PDF document.`,
                                                        'Admin'
                                                    )}
                                                />

                                                {x.isClipMask && (
                                                    <LayerSubOption>
                                                        <Checkbox
                                                            label={T._('Clipped print-ready file', 'Admin')}
                                                            helpTooltip={T._(
                                                                'If checked, the final output will be generated for the clipped area only instead of the full PDF area.',
                                                                'Admin'
                                                            )}
                                                            onChange={(checked) => handleLayerPropChange(x.id, 'isOutputClipped', checked)}
                                                            checked={x.isOutputClipped}
                                                        />
                                                    </LayerSubOption>
                                                )}
                                            </LayerContent>
                                        </Layer>
                                    );
                                })}
                            </LayersList>
                        )}

                        {isBleedLinesMode && selectedArea && (
                            <FormControl>
                                <Checkbox
                                    checked={selectedArea.layers[0].isVisibleInCart}
                                    onChange={(checked) => handleVisibleInCartChange(checked)}
                                    label={T._('Layers visible in the cart', 'Admin')}
                                    helpTooltip={T._(
                                        'If checked, the thumbnail of the customized product in the cart will show the bleed/cut lines as well.',
                                        'Admin'
                                    )}
                                />
                            </FormControl>
                        )}
                    </Stack>

                    <AreaDesignerRightPanelFooter>
                        <PrimaryButton $size='fullWidth' disabled={isLoadingPng} onClick={handleSave}>
                            {T._('Apply', 'Admin')}
                        </PrimaryButton>
                    </AreaDesignerRightPanelFooter>
                </AreaDesignerRightPanel>
            </AreaDesignerLayout>
        </AreaDesignerRoot>
    );
};

const AreaDesigner: FC<AreaDesignerProps> = (props) => {
    return (
        <ValidationStateProvider>
            <AreaDesignerContent {...props} />
        </ValidationStateProvider>
    );
};

export default AreaDesigner;
